import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

export type LegalSection = {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
};

interface LegalPageProps {
  title: string;
  lastUpdated: string;
  intro: string[];
  sections: LegalSection[];
  pageTestId: string;
  documentTitle: string;
}

export default function LegalPage({
  title,
  lastUpdated,
  intro,
  sections,
  pageTestId,
  documentTitle,
}: LegalPageProps) {
  useEffect(() => {
    document.title = `${documentTitle} — WinCampaign`;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [documentTitle]);

  return (
    <div
      data-testid={pageTestId}
      className="bg-white text-[#1a1a24] overflow-x-hidden"
    >
      <Navbar />
      <main>
        <section className="bg-[#f6f3ff] pt-16 pb-12 sm:pt-24 sm:pb-16 border-b border-[#e9e2ff]">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <Link
              to="/"
              data-testid={`${pageTestId}-back`}
              className="inline-flex items-center gap-1.5 text-sm font-nav text-[#673dfc] hover:text-[#30265b] transition-colors mb-6"
            >
              <ArrowLeft size={14} />
              Back to home
            </Link>
            <div className="text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc] mb-3">
              Last Updated: {lastUpdated}
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#30265b] leading-[1.05]">
              {title}
            </h1>
          </div>
        </section>

        <section className="py-14 sm:py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 space-y-5">
            {intro.map((p, i) => (
              <p
                key={`intro-${i}`}
                className="text-gray-700 leading-relaxed text-base sm:text-lg"
              >
                {p}
              </p>
            ))}

            {sections.map((section, sIdx) => (
              <div key={`sec-${sIdx}`} className="pt-6">
                {section.heading && (
                  <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#30265b] leading-tight mb-4">
                    {section.heading}
                  </h2>
                )}
                {section.paragraphs?.map((p, pIdx) => (
                  <p
                    key={`sec-${sIdx}-p-${pIdx}`}
                    className="text-gray-700 leading-relaxed mb-3 text-base"
                  >
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-3 space-y-1.5 pl-5 list-disc marker:text-[#673dfc] text-gray-700 text-base leading-relaxed">
                    {section.bullets.map((b, bIdx) => (
                      <li key={`sec-${sIdx}-b-${bIdx}`}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
